<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import Alert from 'components/Alert.vue';
import CheckboxFormField from 'components/CheckboxFormField.vue';
import PasswordField from 'components/forms/PasswordField.vue';
import UserRegistrationIndustries from 'components/UserRegistrationIndustries.vue';
import { useAnalytics } from 'src/composables/analytics';

const store = useStore();
const { trackEvent } = useAnalytics();

const message = ref({ type: 'success', content: null });
const finished = ref(false);
const loading = ref(false);
const consentInfoContent = ref(null);

const hasMessage = computed(() => message.value && message.value.content);
const loginLink = computed(() => `${location.origin}${location.pathname}?showLogin=1`);

const links = computed(() => store.state.generic.links);

async function submit(values) {
  const consentKeys = ['inquiries', 'personalizedTracking', 'surveys', 'onlineMarketing'];

  // eslint-disable-next-line camelcase
  const { password__confirmation, ...data } = values;
  data.consent = consentKeys.filter((consentKey) => values[consentKey]);

  consentKeys.forEach((consentKey) => {
    delete data[consentKey];
  });

  loading.value = true;
  message.value = {};

  trackEvent('user', 'register', false);

  try {
    await store.dispatch('user/register', data);
    finished.value = true;
  } catch (e) {
    message.value = {
      type: 'error',
      content: e
    };
  }
  loading.value = false;
}

async function close() {
  store.dispatch('user/hideRegistration');
}
</script>

<template>
  <modal
    @close="close"
    :headline="$t('register.modalHeadline')"
    :description="$t('register.modalDescription')">
    <div class="ce-registerForm">
      <div class="ce-modalContent ce-userRegistration-content">
        <alert
          v-if="hasMessage"
          :message="message.content"
          :type="message.type"
          :closable="false" />

        <template v-if="finished">
          <p class="mt0">
            {{ $t('register.successContent') }}
          </p>

          <div class="mt4">
            <button
              class="ce-btn ce-btn-light ce-btn--fullWidth"
              @click.prevent="close">
              {{ $t('register.successCta') }}
            </button>
          </div>
        </template>

        <template v-if="!finished">
          <v-form
            ref="validationObserver"
            tag="div"
            @submit="submit"
            :initial-values="{
              selectedIndustries: [],
              inquiries: false,
              personalizedTracking: false,
              surveys: false,
              onlineMarketing: false
            }">
            <validated-input
              name="firstName"
              :label="$t('register.firstName')"
              rules="required" />

            <validated-input
              name="lastName"
              :label="$t('register.lastName')"
              rules="required" />

            <validated-input
              name="company"
              :label="$t('register.company')"
              rules="required" />

            <validated-input
              name="email"
              :label="$t('register.email')"
              rules="required|email" />

            <country-select
              name="country"
              rules="required"
              :label="$t('countrySelect.label')" />

            <UserRegistrationIndustries />

            <password-field
              name="password"
              :label="$t('register.password')" />

            <div class="ce-userRegistration-consent ms-1 line-height-3 mb4">
              <div>
                {{ $t('register.consent.infoContent') }}
              </div>
              <div class="ce-userRegistration-consent-personalData">
                <p>{{ $t('register.consent.personalData.topInfo') }}</p>

                <div class="mt2">
                  <checkbox-form-field
                    class="ce-field"
                    id="consent_inquiries"
                    name="inquiries"
                    :vertical-centered="false">
                    {{ $t('register.consent.personalData.inquiries') }}
                  </checkbox-form-field>
                  <checkbox-form-field
                    class="ce-field"
                    id="consent_personalizedTracking"
                    name="personalizedTracking"
                    :vertical-centered="false">
                    {{ $t('register.consent.personalData.personalizedTracking') }}

                    <a
                      :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
                      class="ce-userRegistration-consent-infoCta"
                      @click.prevent="consentInfoContent = $t('register.consent.dataProtectionInfo.personalizedTracking')">
                      {{ $t('register.consent.dataProtectionInfo.label') }}
                    </a>.
                  </checkbox-form-field>
                  <checkbox-form-field
                    class="ce-field"
                    id="consent_surveys"
                    name="surveys"
                    :vertical-centered="false">
                    {{ $t('register.consent.personalData.surveys') }}

                    <a
                      :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
                      class="ce-userRegistration-consent-infoCta"
                      @click.prevent="consentInfoContent = $t('register.consent.dataProtectionInfo.surveys')">
                      {{ $t('register.consent.dataProtectionInfo.label') }}
                    </a>.
                  </checkbox-form-field>

                  <checkbox-form-field
                    class="ce-field"
                    id="consent_onlineMarketing"
                    name="onlineMarketing"
                    :vertical-centered="false">
                    {{ $t('register.consent.personalData.onlineMarketing') }}

                    <a
                      :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
                      class="ce-userRegistration-consent-infoCta"
                      @click.prevent="consentInfoContent = $t('register.consent.dataProtectionInfo.onlineMarketing')">
                      {{ $t('register.consent.dataProtectionInfo.label') }}
                    </a>.
                  </checkbox-form-field>
                </div>

                <p>{{ $t('register.consent.personalData.disclaimer') }}</p>

                <modal
                  :show-header="false"
                  v-if="consentInfoContent"
                  @close="consentInfoContent = null">
                  <div class="ce-modalContent">
                    <div v-html="consentInfoContent" />

                    <div>
                      <i18n-t
                        keypath="register.consent.fullDataProtectionStatement.content"
                        scope="global">
                        <a
                          class="ce-userRegistration-consent-infoCta"
                          target="_blank"
                          :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage">
                          {{ $t('register.consent.fullDataProtectionStatement.link') }}
                        </a>
                      </i18n-t>
                    </div>
                  </div>
                </modal>
              </div>
            </div>

            <div>
              <button
                class="ce-btn ce-btn-light ce-btn--fullWidth"
                :disabled="loading"
                type="submit">
                <template v-if="loading">
                  <fa-icon
                    icon="spinner"
                    spin />
                </template>
                <template v-else>
                  {{ $t('register.registerCta') }}
                </template>
              </button>
            </div>

            <div class="mt2 ce-userRegistration-login">
              {{ $t('register.existingAccount') }} <a
                :href="loginLink"
                @click.prevent="$store.dispatch('user/showLogin')">{{ $t('register.loginCta') }}</a>
            </div>
          </v-form>
        </template>
      </div>
    </div>
  </modal>
</template>

<style lang="less">
  @import (reference) '~styles/includes/base';

  .ce-userRegistration-content {
    > div {
      .mb3;
    }

    .ce-pageHeader .ce-pageHeader-main & {
      .ce-userRegistration-login {
        a {
          color: @color-light;
        }
      }
    }

    .ce-userRegistration-consent-personalData {
      > p {
        &:first-child {
          margin-bottom: @space-1;
        }
        &:last-child {
          margin-top: @space-1;
        }
      }

      > div {
        line-height: 1.5;

        > div {
          display: flex;
          align-items: center;

          input {
            margin-right: @space-1;
          }
        }
      }
    }

    .ce-userRegistration-industrySelect {
      display: flex;
      align-items: center;
      margin: @space-2;

      label {
        margin: 0;
        .noselect;
      }

      input {
        margin-right: @space-1;
      }
    }

    .ce-userRegistration-consent {
      .ce-field.ce-checkbox {
        margin-bottom: @space-2;
      }

      .ce-userRegistration-consent-infoCta {
        font-weight: 400;
        text-decoration: underline;
        color: @color-text-base !important;
      }

      .tooltip {
        .tooltip-inner {
          max-width: 100%;
          .ms(-2);
          font-weight: 400;
        }
      }
    }
  }
</style>
